import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import type { AgGridReact } from "ag-grid-react";
import { Map as ImmutableMap } from "immutable";
import { useMemo, useState } from "react";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { platformToast } from "$root/notification-system/toast";
import type { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import type { ReviewInstrument } from "./const";
import { ReviewHeader, type ReviewHeaderProps } from "./ReviewHeader";
import { ReviewInstrumentsTable } from "./ReviewInstrumentsTable";
import { convertReviewInstrumentToReviewTicker, needsReview, sanitizeReviewInstrument } from "./reviewRows";

export type WeightReviewProps = {
	rows: ReviewInstrument[];
	entity: ReviewEntity;
	canEditComposition: boolean;
	header: Omit<ReviewHeaderProps, "save">;
	onSave(composition: ReviewTicker[]): Promise<void>;
	onDirty(): void;
};

/** The review of a composition held in weights. */
export function WeightReview({
	rows: initialRows,
	entity,
	canEditComposition,
	header,
	onSave,
	onDirty,
}: WeightReviewProps): React.JSX.Element {
	const [instrumentReviewMap, setInstrumentReviewMap] = useState(() => toMap(initialRows));
	const [loadedRows, setLoadedRows] = useState(initialRows);
	if (loadedRows !== initialRows) {
		setLoadedRows(initialRows);
		setInstrumentReviewMap(toMap(initialRows));
	}
	const [gridEl, setGridEl] = useState<AgGridReact<ReviewInstrument> | null>(null);

	const classifications = useAtomValue(userClassifications$);
	const columns = useMemo(() => {
		if (entity === "UNIVERSE") {
			return columnsDefinition.instrumentUniverseReviewComposition(
				classifications ?? [],
				canEditComposition ? "edit" : "read",
			);
		}
		return columnsDefinition.instrumentReviewComposition(classifications ?? [], canEditComposition ? "edit" : "read");
	}, [canEditComposition, entity, classifications]);

	const persistColProps = agExtra.usePersistColState(
		{
			BENCHMARK: persistedColStateKeys["tableV3-universe-composition-review"],
			TARGET_INVESTMENT: persistedColStateKeys["tableV3-universe-composition-review"],
			INVESTMENT: persistedColStateKeys["tableV3-investment-composition-review"],
			UNIVERSE: persistedColStateKeys["tableV3-universe-composition-review"],
		}[entity],
	);

	const mergeRows = (changed: ReviewInstrument[]) => {
		setInstrumentReviewMap((latest) => latest.merge(changed.map((instrument) => [instrument.id, instrument])));
		onDirty();
	};
	const patchRows = (ids: string[], patch: (row: ReviewInstrument) => Partial<ReviewInstrument>) => {
		setInstrumentReviewMap((latest) =>
			latest.merge(
				ids.flatMap((id): Array<[string, ReviewInstrument]> => {
					const row = latest.get(id);
					return row ? [[id, { ...row, ...patch(row) }]] : [];
				}),
			),
		);
		onDirty();
	};

	const save = async () => {
		const rows = instrumentReviewMap.valueSeq().toArray();
		if (rows.some(needsReview)) {
			const instrumentsToUpdate: Array<ReviewInstrument> = [];
			for (const instrument of rows) {
				if (
					!needsReview(instrument) &&
					instrument.issue === "RISK_MODEL_SUGGESTION_REQUIRES_REVIEW" &&
					instrument.hasBeenReviewed === false
				) {
					instrumentsToUpdate.push({ ...instrument, hasBeenReviewed: true });
				}
			}
			gridEl?.api.applyTransactionAsync({ update: instrumentsToUpdate });
			return platformToast({
				children: "You need to review your composition",
				icon: "Portfolio",
				severity: "warning",
			});
		}
		await onSave(rows.filter(sanitizeReviewInstrument).map(convertReviewInstrumentToReviewTicker));
	};

	return (
		<>
			<ReviewHeader {...header} save={canEditComposition ? { kind: "ready", onSave: save } : undefined} />
			<ReviewInstrumentsTable
				{...persistColProps}
				rows={initialRows}
				columns={columns}
				agGridRef={setGridEl}
				onDelete={(deleted) => patchRows(deleted, () => ({ deleted: true }))}
				onRestore={(restored) => patchRows(restored, () => ({ deleted: false }))}
				onProxy={(proxied) => {
					const byId = new Map(proxied.map((x) => [x.id, x.proxies]));
					patchRows(
						proxied.map((x) => x.id),
						(row) => ({ proxies: byId.get(row.id), hasBeenReviewed: true }),
					);
				}}
				onResolveIssue={(issues) => {
					const byId = new Map(issues.map((x) => [x.id, x]));
					patchRows(
						issues.map((x) => x.id),
						(row) => {
							const issue = byId.get(row.id);
							return { proxies: issue?.proxies, hasBeenReviewed: issue?.hasBeenReviewed, newAlias: issue?.newAlias };
						},
					);
				}}
				onRowsValueChange={mergeRows}
			/>
		</>
	);
}

function toMap(rows: ReviewInstrument[]): ImmutableMap<string, ReviewInstrument> {
	return ImmutableMap(rows.map((x) => [x.id, x]));
}
